//
// # Annotations — Render Tier
//
// The DOM-free render side of annotations: the entity wrapper, the graphics
// entity tree, its redraw, shape accessors, group bookkeeping, and add/remove.
// Carries no InteractionHost/modes, so the render path (and the headless server)
// can draw annotations. The interaction tier `Annotations` (EditorCore) wraps
// this.
//

// Local Imports
import type { RedrawRequest } from "../../redraw";
import * as style from "../../style";
import { EntityKind } from "../../entity";
import { Primitive } from "../../primitives";
import { GraphicsEntity } from "./graphics";
import type { ChildInterface, RenderContentBase } from "../base_render";
import { DrawTo } from "../../draw";

// Annotation Graphics Element Entity Wrapper
export class Annotation implements ChildInterface {
  //
  readonly entityKind = EntityKind.Annotation;
  constructor(readonly prim: Primitive) {}

  // Forward all this to our inner prim.
  // NOTE: `data` is consumed dynamically by the change pipeline
  // (Editor.entityToModelData / getEntityId access `entity.data` through
  // untyped `any`/`unknown` params). It looks unused to static analysis but
  // is required for symbol graphics to serialize through the change pipeline —
  // do not remove it.
  get data() {
    return this.prim.data;
  }
  highlight = (state: boolean) => this.prim.highlight(state);
  invalidate = (): RedrawRequest => this.prim.invalidate();
  redraw = (to: DrawTo) => this.prim.redraw(to);
}

// # Render tier
//
// The drawable + data side of annotations: the graphics entity tree, its
// redraw, the shape accessors, group bookkeeping, and add/remove. Carries no
// InteractionHost, so the render path (and the server) can draw annotations.
export class AnnotationsRender {
  readonly graphicsEntity: GraphicsEntity;

  constructor(
    // The entity-tree root the graphics parent under (the render tier).
    parent: RenderContentBase,
    readonly table: style.StyleTable = DEFAULT_STYLE_TABLE
  ) {
    this.graphicsEntity = GraphicsEntity.new(parent, table);
  }

  // Grouping for moving/deleting multiple elements together.
  // Rebuilt from the persisted groupId field on each graphics element.
  private readonly elementToGroup = new Map<number, number>();
  private readonly groupToElements = new Map<number, Set<number>>();

  // Register an element in the group maps based on its groupId.
  registerGroup = (data: { id?: number; groupId?: number }): void => {
    const { id, groupId } = data;
    if (id === undefined || groupId === undefined) return;
    this.elementToGroup.set(id, groupId);
    let group = this.groupToElements.get(groupId);
    if (!group) {
      group = new Set<number>();
      this.groupToElements.set(groupId, group);
    }
    group.add(id);
  };

  // Remove an element from the group maps.
  private unregisterGroup = (data: { id?: number }): void => {
    const { id } = data;
    if (id === undefined) return;
    const groupId = this.elementToGroup.get(id);
    if (groupId === undefined) return;
    this.elementToGroup.delete(id);
    const group = this.groupToElements.get(groupId);
    if (group) {
      group.delete(id);
      if (group.size === 0) this.groupToElements.delete(groupId);
    }
  };

  // Get all element IDs in the same group as the given element, or null if ungrouped.
  getGroup = (elementId: number): Set<number> | null => {
    const groupId = this.elementToGroup.get(elementId);
    if (groupId === undefined) return null;
    return this.groupToElements.get(groupId) ?? null;
  };

  // Find the primitive entity for a given element ID across all entity sets.
  findPrimitive = (elementId: number): Primitive | null => {
    for (const r of this.rects) if (r.data.id === elementId) return r;
    for (const p of this.polygons) if (p.data.id === elementId) return p;
    for (const p of this.paths) if (p.data.id === elementId) return p;
    for (const e of this.ellipses) if (e.data.id === elementId) return e;
    for (const a of this.arcs) if (a.data.id === elementId) return a;
    for (const t of this.texts) if (t.data.id === elementId) return t;
    return null;
  };

  // Add a child
  // Note `anno` itself is dropped here; we forward its internal `data` along for drawing.
  // Also registers the element in the group maps if it has a groupId.
  add = (anno: Annotation): unknown => {
    this.registerGroup(anno.prim.data);
    return this.graphicsEntity.addEntity(anno.prim);
  };

  // Remove a child and clean up its group membership.
  remove = (anno: Annotation): boolean => {
    this.unregisterGroup(anno.prim.data);
    return this.graphicsEntity.remove(anno.prim);
  };

  // Forward to our graphics
  redraw = (to: DrawTo) => this.graphicsEntity.redraw(to);

  get rects() {
    return this.graphicsEntity.rects;
  }
  get polygons() {
    return this.graphicsEntity.polygons;
  }
  get paths() {
    return this.graphicsEntity.paths;
  }
  get ellipses() {
    return this.graphicsEntity.ellipses;
  }
  get arcs() {
    return this.graphicsEntity.arcs;
  }
  get texts() {
    return this.graphicsEntity.texts;
  }
}

// # Interaction tier
//
// Editing operations (add / move / draw modes) over the render tier. Holds the
// render side (`render`) plus the InteractionHost and content it logs changes
// against. Forwards the data/draw surface to `render` so callers (and the draw

const { darkgrey, white, black } = style.Color;

// Default annotation-graphics styling
export const DEFAULT_STYLE_TABLE: style.StyleTable = {
  shape: {
    //       light light    dark  dark
    //       no    hilited  no    hilited
    fill: style.Special.None,
    stroke: [darkgrey, black, white, white],
    linewidth: [2, 3, 2, 3],
    cap: "round",
    join: "round",
  },
  text: {
    family: style.THE_FONT,
    style: "normal",
    weight: [700, 700, 700, 700], // Typical value for "bold"
    size: [16, 16, 16, 16],
    fill: [darkgrey, black, white, white],
    stroke: style.Special.None,
  },
};
